import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FieldRenderProps } from "react-final-form";
import { ValueType } from "react-select";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import AddIcon from "@material-ui/icons/Add";

import { Button, FormLabel } from "@material-ui/core";

import ContentStudioMovementsStore from "stores/ContentStudioMovementsStore";

import Popup from "components/Popup";
import withFieldControl from "components/FormControl/FieldControl";
import Placeholder from "components/Placeholder";

import MovementForm from "./MovementForm";
import MovementCard from "./MovementCard";

import { reorder } from "utils/calculation";

import { DictionaryItem } from "constants/models";

import {
  Container,
  ControlHeader,
  ControlContainer,
  MovementCards,
  Row,
} from "./styles";

type Props = {};

@observer
class MovementsControl extends React.Component<FieldRenderProps<any> & Props> {
  @observable isPopupOpen: boolean = false;

  @computed
  get values() {
    const { input } = this.props;
    const value = input?.value || [];

    return value.map((item: any) => ({ label: item.title, value: item }));
  }

  @computed get filteredOptions() {
    return ContentStudioMovementsStore.movementsOptions.filter(
      option => !this.values.find(({ value }) => value.id === option.value.id),
    );
  }

  onChange = (items: DictionaryItem[] = []) => {
    const {
      input: { onChange },
    } = this.props;

    const value = items.map(item => item.value);
    const event: any = { target: { value } };
    onChange(event);
  };

  onRemoveItem = (option: ValueType<DictionaryItem>) => () => {
    const values = this.values.filter(
      item => item.value.id !== option.value.id,
    );
    this.onChange(values);
  };

  closePopup = () => {
    this.isPopupOpen = false;
  };

  openPopup = () => {
    this.isPopupOpen = true;
  };

  saveNewMovements = (newEquipment: DictionaryItem[]) => {
    this.onChange([...this.values, ...newEquipment]);
    this.closePopup();
  };

  onDragEnd = async (result: DropResult) => {
    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    const reorderedMovements = reorder(this.values, startIndex, endIndex);
    this.onChange(reorderedMovements);
  };

  render() {
    return (
      <>
        {this.isPopupOpen && (
          <Popup
            onClose={this.closePopup}
            canCloseByOuterClick={false}
            withoutExitButton
          >
            <MovementForm
              options={this.filteredOptions}
              onClose={this.closePopup}
              onSave={this.saveNewMovements}
            />
          </Popup>
        )}
        <Container>
          <Row>
            <ControlHeader>
              <FormLabel>Movements</FormLabel>
              <ControlContainer>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={this.openPopup}
                >
                  Add Movements
                </Button>
              </ControlContainer>
            </ControlHeader>
          </Row>

          {this.values.length ? (
            <Row>
              <MovementCards>
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {this.values.map((item, index) => (
                          <Draggable
                            key={item.value.id}
                            draggableId={item.value.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <MovementCard
                                key={item.value.id}
                                title={item.label}
                                duration={item.value.durationText}
                                image={item.value.imageUrl}
                                onRemove={this.onRemoveItem(item)}
                                dragHandleProps={provided.dragHandleProps}
                                innerRef={provided.innerRef}
                                isDragging={snapshot.isDragging}
                                {...provided.draggableProps}
                                isEditMode
                              />
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </MovementCards>
            </Row>
          ) : (
            <Placeholder
              height={132}
              width={132}
              text={"No movements added yet."}
            />
          )}
        </Container>
      </>
    );
  }
}

export default withFieldControl(MovementsControl);
